import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CheckCircle2, Loader, Clock, BadgeCheck, Banknote, Search, PlusCircle, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { StatCard, StatusBadge, SalesIdChip, Card, EmptyState, Loading } from "../../components/common";
import { api } from "../../lib/api";
import { pkr, fmtDate, ORDER_STATUS, COMMISSION_STATUS } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

export default function ExecDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/exec").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="space-y-8">
      <div className="rounded-2xl bg-[#0B192C] p-6 sm:p-8 text-white">
        <p className="text-sm text-slate-400">Assalam-o-Alaikum,</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{user.name}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-300">Your Sales ID</span>
          <SalesIdChip id={d.sales_id} dark />
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild className="h-11 bg-emerald-500 text-[#0B192C] hover:bg-emerald-400" data-testid="dash-book-order-btn"><Link to="/app/book"><PlusCircle className="mr-2 h-4 w-4" />Book an Order</Link></Button>
          <Button asChild variant="outline" className="h-11 border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white" data-testid="dash-find-deals-btn"><Link to="/app/deals"><Search className="mr-2 h-4 w-4" />Find Deals</Link></Button>
        </div>
      </div>

      {d.recovery_due > 0 && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="recovery-due-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          {pkr(d.recovery_due)} of commission already paid to you was reversed because the order was cancelled or refunded. The administrator will contact you about recovery.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
        <StatCard testid="stat-total-orders" label="Total orders" value={d.total_orders} icon={ClipboardList} />
        <StatCard testid="stat-completed-orders" label="Completed" value={d.completed_orders} icon={CheckCircle2} tone="green" />
        <StatCard testid="stat-in-progress" label="In progress" value={d.in_progress_orders} icon={Loader} />
        <StatCard testid="stat-pending-commission" label="Pending commission" value={pkr(d.pending_commission)} icon={Clock} tone="amber" hint="Awaiting completion / review" />
        <StatCard testid="stat-approved-commission" label="Approved, payable" value={pkr(d.approved_commission)} icon={BadgeCheck} tone="green" hint="Will be paid to you" />
        <StatCard testid="stat-paid-commission" label="Total paid" value={pkr(d.paid_commission)} icon={Banknote} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Recent orders</h2><Link to="/app/orders" className="text-sm text-emerald-700 hover:underline">View all</Link></div>
          {d.recent_orders.length === 0 ? <EmptyState title="No orders yet" message="Find a deal and book your first customer order to start earning." action={<Button asChild><Link to="/app/deals">Find Deals</Link></Button>} /> : (
            <ul className="divide-y">
              {d.recent_orders.map((o) => (
                <li key={o.id}><Link to={`/app/orders/${o.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50" data-testid={`recent-order-${o.order_no}`}>
                  <div className="min-w-0"><p className="font-mono text-sm font-bold">{o.order_no}</p><p className="truncate text-sm text-slate-500">{o.deal.name} · {o.customer.name}</p></div>
                  <div className="text-right"><StatusBadge map={ORDER_STATUS} value={o.status} /><p className="mt-1 text-xs text-slate-500">{fmtDate(o.created_at)}</p></div>
                </Link></li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Recent commissions</h2><Link to="/app/commissions" className="text-sm text-emerald-700 hover:underline">View all</Link></div>
          {d.recent_commissions.length === 0 ? <EmptyState title="No commissions yet" message="Each order you book creates a commission record here." /> : (
            <ul className="divide-y">
              {d.recent_commissions.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0"><p className="font-mono text-sm font-bold">{c.order_no}</p><p className="truncate text-sm text-slate-500">{c.deal_name}</p></div>
                  <div className="text-right"><p className="font-mono font-bold">{pkr(c.amount)}</p><StatusBadge map={COMMISSION_STATUS} value={c.status} /></div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
